import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { storage } from "@/lib/storage";
import toast from "react-hot-toast";
import {
  LogOut, ChevronDown, UtensilsCrossed, ShieldCheck,
  CalendarDays, Phone, Menu, X,
  UserPlus,
  BadgePercent,
  Cake,
  Smartphone,
  CreditCard,
  Repeat,
  History,
  Megaphone,
  LayoutDashboard, UsersRound, MapPin, Settings2, BadgeCheck,
  UserRoundCheck, ChartNoAxesCombined, PackageCheck,
  PanelLeftClose, PanelLeftOpen,
  Sun,
  Moon,
} from "lucide-react";
import DeliveryLiveTracker from "@/components/delivery/DeliveryLiveTracker";
import { useDispatch, useSelector } from 'react-redux';
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { RootState } from "@/lib/store";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { setRefreshTokenCookie } from "@/lib/clientCookie";
import { logoutService } from '@/services/auth';
import { clearPermissions } from '@/lib/store/slices/PermissionsSlice';

function getDashboardPath(role?: string): string {
  const r = role?.toLowerCase().replace(/[\s_]+/g, '');
  if (r === 'parent') return '/dashboard';
  if (r === 'superuser') return '/superuser-dashboard';
  if (r === 'zonemanager') return '/zone-manager-dashboard';
  if (r === 'branchincharge') return '/branch-incharge-dashboard';
  return '/admin-dashboard';
}

const NAV_ITEMS: { label: string; path: string; screens: string[]; roles?: string[] }[] = [
  { label: "Dashboard", path: "__dashboard__", screens: [] },
  { label: "Users", path: "/user-management", screens: ["employees", "resgistered profile"] },
  { label: "Track Delivery", path: "/track-delivery", screens: ["location tracking"] },
  { label: "Masters", path: "/masters", screens: ["states", "cities", "schools", "zones", "branches", "roles", "ranges"] },
  { label: "Verifications", path: "/verification", screens: ["verified child"] },
  { label: "Pending Users", path: "/payment-pending-users", screens: ["pending payments"] },
  { label: "Leads", path: "/leads", screens: ["leads"] },
  { label: "Assign Delivery", path: "/assignment", screens: ["delivery assign"] },
  { label: "Pickups", path: "/pickups", screens: ["pickups"], roles: ["branchincharge", "zonemanager"] },
  { label: "Subscription Data", path: "/subscription-data", screens: ["subscription data"] },
  { label: "Reports", path: "/reports", screens: [] },
];

const MORE_ITEMS = [
  { key: "canSeeLunchUpdates", screen: "lunch update", path: "/lunch-updates", label: "Lunch Updates", Icon: UtensilsCrossed, color: "text-mlb-gold" },
  { key: "canSeeHolidays", screen: "holidays", path: "/holidays", label: "Holidays", Icon: CalendarDays, color: "text-mlb-gold" },
{ key: "canSeeNotices", screen: "notices", path: "/notices", label: "Notices", Icon: Megaphone, color: "text-mlb-gold" },
  { key: "canSeeSubscriptions", label: "Subscriptions", path: "/pricing-management", screen: "plans", Icon: Repeat, color: "text-mlb-green" },
  { key: "canSeeHistory", label: "History", path: "/history", screen: "subscriptionplanshistory", Icon: History, color: "text-mlb-green" },
  { key: "canSeeTeleRecord", screen: "telerecords", path: "/tele-record", label: "Tele Record", Icon: Phone, color: "text-mlb-gold" },
  // { key: "canSeeAssignDelivery", screen: "delivery assign", path: "/assignment", label: "Assign Delivery", Icon: UserPlus, color: "text-mlb-green" },
  { key: "canSeeReferral", screen: "referral", path: "/referral", label: "Referrals", Icon: UserPlus, color: "text-mlb-green" },
  { key: "canSeeOffers", screen: "offers", path: "/offers", label: "Offers", Icon: BadgePercent, color: "text-mlb-green" },
  { key: "canSeeSpecialOffers", screen: "special offer", path: "/special-offers", label: "Special Offers", Icon: BadgePercent, color: "text-mlb-green" },
  { key: "canSeeTrailOffers", screen: "trails offer", path: "/trail-offer", label: "Trail Offers", Icon: BadgePercent, color: "text-mlb-green" },
  { key: "canSeeUpcomingBirthdays", screen: "upcoming birthdays", path: "/upcoming-birthdays", label: "Birthdays", Icon: Cake, color: "text-mlb-green" },
  { key: "canSeePaymentSummary", screen: "payment summary", path: "/payment-summary", label: "Payments Data", Icon: CreditCard, color: "text-mlb-green" },
  { key: "canSeePaymentList", screen: "payment list", path: "/payment-list", label: "Payments", Icon: CreditCard, color: "text-mlb-green" },
] as const;

export default function AdminHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userDetails = useCurrentUser();
  const dispatch = useDispatch();
  const { loaded: permissionsLoaded, data: permissions } = useSelector((state: RootState) => state.permissions);
  const noPermissions = !permissionsLoaded;

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('mlbSidebarCollapsed') === 'true');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('mlbTheme') === 'dark');
  useEffect(() => {
    document.documentElement.classList.toggle('theme-dark', darkMode);
    localStorage.setItem('mlbTheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    document.body.classList.add('admin-shell');
    document.body.style.setProperty('--sidebar-width', sidebarCollapsed ? '76px' : '240px');
    return () => {
      document.body.classList.remove('admin-shell');
      document.body.style.removeProperty('--sidebar-width');
    };
  }, [sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      localStorage.setItem('mlbSidebarCollapsed', String(next));
      return next;
    });
  };

  const role = (userDetails?.role as string | undefined) ?? '';
  const dashboardPath = getDashboardPath(role);
  const normalizedRole = role.toLowerCase().replace(/[\s_]+/g, '');
  let isLoggingOut = false;

  // Close mobile menu on route change
  useEffect(() => { setShowMobileMenu(false); }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMobileMenu]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        await logoutService(refreshToken);
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      toast.success("Logged out successfully");
      navigate("/login", { replace: true });

      isLoggingOut = false;
    }
  };

  const canSeeNav = (screens: string[]) =>
    noPermissions || screens.some(k => k in permissions && permissions[k]?.Can_View !== false);

  const visibleNavItems = NAV_ITEMS
    .map(item => ({ ...item, path: item.path === '__dashboard__' ? dashboardPath : item.path }))
    .filter(item => !item.roles || item.roles.includes(normalizedRole))
    .filter(item => item.screens.length === 0 || canSeeNav(item.screens));

  const isSuperuser = !!(userDetails as any)?.isSuperuser;

  const visibleMoreItems = MORE_ITEMS.filter(item =>
    noPermissions || (item.screen in permissions && permissions[item.screen]?.Can_View !== false)
  );

  const primaryIcons = [LayoutDashboard, UsersRound, MapPin, Settings2, BadgeCheck, UserRoundCheck, UserRoundCheck, ChartNoAxesCombined, UserRoundCheck, PackageCheck, ChartNoAxesCombined, PackageCheck];

  return (
    <>
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 z-50 flex-col border-r border-white/10 transition-[width] duration-300 ${sidebarCollapsed ? 'w-[76px]' : 'w-60'}`}
        style={{ background: '#17202d' }}
      >
        <div className="flex h-[74px] items-center gap-3 border-b border-white/10 px-3">
          <Link to={dashboardPath} className={`flex min-w-0 items-center gap-2 ${sidebarCollapsed ? 'justify-center' : 'flex-1'}`}>
            <img src="/mlblogo.jpeg" alt="Mera Lunch Box" className="h-11 w-11 flex-shrink-0 rounded-xl border border-mlb-gold/40 object-cover" />
            {!sidebarCollapsed && <span className="truncate text-sm font-bold text-white">Mera Lunch Box</span>}
          </Link>
          <button onClick={toggleSidebar} className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white ${sidebarCollapsed ? 'absolute right-[-42px] top-5 bg-[#17202d]' : ''}`} aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4 scrollbar-thin">
          {visibleNavItems.map((item, index) => {
            const active = pathname === item.path;
            return <Link key={item.path} to={item.path} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition ${active ? 'bg-mlb-gold text-[#17202d]' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
              {(() => { const Icon = primaryIcons[index] ?? ChartNoAxesCombined; return <Icon className="h-4 w-4 flex-shrink-0" />; })()}
              {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
            </Link>;
          })}
          {!sidebarCollapsed && <p className="px-3 pb-1 pt-5 text-[10px] font-bold uppercase tracking-widest text-white/30">More</p>}
          {visibleMoreItems.map(({ path, label, Icon, color }) => {
            const active = pathname === path;
            return <Link key={path} to={path} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition ${active ? 'bg-mlb-gold text-[#17202d]' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
              <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-[#17202d]' : color}`} />
              {!sidebarCollapsed && <span className="truncate">{label}</span>}
            </Link>;
          })}
        </nav>
        <div className="border-t border-white/10 p-2">
          <button onClick={() => setDarkMode(prev => !prev)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-white/70 transition hover:bg-white/10 hover:text-white" title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            {darkMode ? <Sun className="h-4 w-4 text-mlb-gold" /> : <Moon className="h-4 w-4 text-mlb-gold" />}
            {!sidebarCollapsed && <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>}
          </button>
          <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-red-300 transition hover:bg-red-400/10 hover:text-red-200" title="Logout">
            <LogOut className="h-4 w-4" />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <header className="sticky top-0 z-40" style={{ background: 'linear-gradient(135deg, #1e2530 0%, #2d3748 50%, #1e2530 100%)', boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05)' }}>

        {/* Top accent line */}
        <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #d4a017, #f0c040, #d4a017, transparent)' }} />

        <div className="max-w-screen-3xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">

          <Link to={dashboardPath} className="absolute left-1/2 hidden -translate-x-1/2 text-base font-extrabold tracking-tight text-white lg:block">Mera Lunch Box</Link>
          <Link to={dashboardPath} className="text-sm font-bold text-white lg:hidden">Mera Lunch Box</Link>
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            {/* <button type="button" onClick={toggleSidebar} className="hidden h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white lg:flex" aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button> */}
            <button type="button" onClick={() => setDarkMode(prev => !prev)} className="hidden h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white sm:flex" aria-label="Toggle theme" title="Toggle theme">
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {/* User menu — desktop */}
            <div className="relative group hidden sm:block">
              <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <div className="w-8 h-8 bg-mlb-gold rounded-full flex items-center justify-center text-sm font-bold text-[#1e2530] flex-shrink-0">
                  {(userDetails?.username as string | undefined)?.[0]?.toUpperCase()}
                </div>
                <div className="text-left hidden md:block">
                  <p className="text-sm text-white font-semibold leading-tight">{userDetails?.username as string}</p>
                  {role && <p className="text-[10px] text-mlb-gold leading-tight capitalize">{role}</p>}
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-white/50" />
              </button>

              <div className="absolute right-0 mt-2 w-52 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" style={{ background: '#1e2530', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                <div className="p-4 border-b border-white/8">
                  <p className="text-sm font-bold text-white">{userDetails?.username as string}</p>
                  <p className="text-xs text-white/50 truncate mt-0.5">{(userDetails as any)?.email}</p>
                  {role && <span className="mt-2 inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-mlb-gold text-[#1e2530] capitalize">{role}</span>}
                </div>
                <div className="p-2">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/8 hover:text-red-300 rounded-lg transition-colors">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            </div>

            {/* Hamburger — mobile/tablet */}
            <button
              onClick={() => setShowMobileMenu(prev => !prev)}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer — absolute so it overlays content without pushing it down */}
        {showMobileMenu && (
          <div className="lg:hidden absolute top-full left-0 right-0 z-50 border-t border-white/8 shadow-2xl max-h-[calc(100vh-60px)] overflow-y-auto" style={{ background: '#1a2030' }}>
            <div className="max-w-screen-2xl mx-auto px-4 py-3 space-y-1">

              {/* User info (mobile) */}
              <div className="flex items-center gap-3 p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="w-10 h-10 bg-mlb-gold rounded-full flex items-center justify-center text-base font-bold text-[#1e2530] flex-shrink-0">
                  {(userDetails?.username as string | undefined)?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{userDetails?.username as string}</p>
                  {role && <p className="text-xs text-mlb-gold capitalize">{role}</p>}
                </div>
                <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-white/10 transition-colors">
                  <LogOut className="w-3.5 h-3.5" />
                  Logout
                </button>
              </div>

              {/* Nav items */}
              {visibleNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === item.path
                      ? 'bg-mlb-gold text-[#1e2530]'
                      : 'text-white/75 hover:text-white hover:bg-white/8'
                    }`}
                >
                  {item.label}
                </Link>
              ))}

              {/* More items */}
              {(visibleMoreItems.length > 0 || isSuperuser) && (
                <div className="pt-2 border-t border-white/8 mt-2 space-y-1">
                  <p className="px-4 text-[10px] font-bold text-white/30 uppercase tracking-widest py-1">More</p>
                  {visibleMoreItems.map(({ path, label, Icon, color }) => (
                    <Link
                      key={path}
                      to={path}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === path ? 'bg-mlb-gold text-[#1e2530]' : 'text-white/75 hover:text-white hover:bg-white/8'
                        }`}
                    >
                      <Icon className={`w-4 h-4 ${pathname === path ? 'text-[#1e2530]' : color}`} />
                      {label}
                    </Link>
                  ))}
                  {isSuperuser && (
                    <>
                      <Link
                        to="/mobile-updates"
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === '/mobile-updates' ? 'bg-mlb-gold text-[#1e2530]' : 'text-white/75 hover:text-white hover:bg-white/8'
                          }`}
                      >
                        <Smartphone className={`w-4 h-4 ${pathname === '/mobile-updates' ? 'text-[#1e2530]' : 'text-mlb-green'}`} />
                        Mobile Updates
                      </Link>
                      <Link
                        to="/permissions"
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === '/permissions' ? 'bg-mlb-gold text-[#1e2530]' : 'text-white/75 hover:text-white hover:bg-white/8'
                          }`}
                      >
                        <ShieldCheck className={`w-4 h-4 ${pathname === '/permissions' ? 'text-[#1e2530]' : 'text-mlb-green'}`} />
                        Permissions
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      <DeliveryLiveTracker />
    </>
  );
}